/// <amd-module name="Test/Analytics/TestInsightsHistoryList/Sources/TestInsightsTestHistoryListSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { areReleaseEntitiesRemoved } from "Test/Analytics/Common/Utils/FeatureFlagUtils";
import { convertValueToDisplayString } from "Test/Analytics/Common/Utils/StringFormattingUtility";
import * as Resources from "Test/Analytics/TestInsightsHistoryList/Resources";
import {
    ITestHistoryListData,
    ITestInsightsTestHistoryListItem,
    ITestObjectReference,
    ITestResultIdentifier
} from "Test/Analytics/TestInsightsHistoryList/Types";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";

export class TestInsightsTestHistoryListSource extends TestReportSource {
    private static readonly ODataNextLink: string = "@odata.nextLink";

    constructor(pageContext: IVssPageContext) {
        super("TestResultsAnalytics_InContext_TestHistoryReport", pageContext);
    }

    public async queryTestHistoryList(
        testReportContext: CommonTypes.ITestReportContext,
        testContext: CommonTypes.ITestContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITestHistoryListData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;

            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            let expandStr = "Branch($select = BranchName)";

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            const filterStr: string =
                `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                    confValues.trendBy,
                    confValues.period
                )} and ${this._getTestContextFilter(testContext.testIdentifier)}` +
                ` ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            let selectStr = "Outcome,CompletedDate,DurationSeconds,TestResultId,TestRunId";

            // Only when TestHistoryList is in a release context, fetch environment Id
            if (testReportContext.contextType === TCMContracts.TestResultsContextType.Release) {
                if (areReleaseEntitiesRemoved(this.pageContext)) {
                    selectStr += ",ReleaseStageId";
                } else {
                    expandStr += `,${this._getReleaseEnvironmentDefinitionExpandString()}`;
                }
            }

            queryOptions.$expand = `${expandStr}`;
            queryOptions.$apply = `${filterStr}`;
            queryOptions.$select = `${selectStr}`;
            queryOptions.$orderby = `CompletedDate desc`;

            const responseData: CommonTypes.IODataQueryResponse | null = await this.queryOData(queryOptions, true);
            return this._convertToHistoryListData(responseData, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    public async queryTestHistoryListWithNextToken(
        confValues: CommonTypes.IReportConfiguration,
        nextPageToken?: CommonTypes.INextDataPageToken
    ): Promise<ITestHistoryListData> {
        let responsePromise: Promise<CommonTypes.IODataQueryResponse> | null = null;
        if (nextPageToken && nextPageToken.token) {
            responsePromise = this.queryODataByUrl(nextPageToken.token);
        }
        const responseData: CommonTypes.IODataQueryResponse | null = await responsePromise;
        return this._convertToHistoryListData(responseData, confValues);
    }

    private _convertToHistoryListData(
        responseData: CommonTypes.IODataQueryResponse | null,
        confValues: CommonTypes.IReportConfiguration
    ): ITestHistoryListData {
        if (!responseData || !responseData.value) {
            throw new Error("Data is empty");
        }

        const testHistoryListItems = (responseData.value as any[]).map(d => {
            const listItem = {
                itemkey: { testRunId: d.TestRunId, testResultId: d.TestResultId } as ITestResultIdentifier,
                itemName: d.TestResultId as string,
                outcome: d.Outcome as string,
                date: d.CompletedDate ? convertValueToDisplayString(new Date(d.CompletedDate)) : Resources.NotAvailable,
                duration: d.DurationSeconds ? d.DurationSeconds : 0,
                branch: d.Branch && d.Branch.BranchName ? d.Branch.BranchName : Resources.NoBranchText
            } as ITestInsightsTestHistoryListItem;

            if (areReleaseEntitiesRemoved(this.pageContext)) {
                listItem.environmentRef = {
                    id: d.ReleaseStageId || 0,
                    name: ""
                };
            } else {
                listItem.environmentRef = {
                    id: d.ReleaseStage && d.ReleaseStage.ReleaseStageId ? d.ReleaseStage.ReleaseStageId : 0,
                    name: ""
                } as ITestObjectReference;
            }

            return listItem;
        });

        return {
            testHistoryListItems: testHistoryListItems,
            confValues: confValues,
            isCachedData: responseData.isCachedData,
            nextPageToken: { token: (responseData as any)[TestInsightsTestHistoryListSource.ODataNextLink] }
        } as ITestHistoryListData;
    }

    private _getTestContextFilter(testIdentifier: string): string {
        return `TestSK eq ${testIdentifier}`;
    }

    private _getReleaseEnvironmentDefinitionExpandString(): string {
        return `ReleaseStage($select = ReleaseStageId)`;
    }
}
